import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, User, MapPin, Settings, Link as LinkIcon, Image, Users, Upload, Plus, Edit, Power, PowerOff, ShieldCheck, Shield, MessageCircle } from 'lucide-react'
import organizadoresService from '../../services/organizadoresService'
import usuariosService from '../../services/usuariosService'
import clientesService from '../../services/clientesService'
import { formatCpfCnpj, formatCep } from '../../utils/masks'
import { fetchCEP } from '../../utils/validators'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import Usuarios from '../configuracoes/Usuarios'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const fluxoOptions = [
  { value: 'comissao_primeira_parcela', label: 'Comissão na Primeira Parcela' },
  { value: 'gta_nota', label: 'GTA + Nota' },
  { value: 'comissao_separada', label: 'Comissão Separada' },
  { value: 'comissao_parcelada', label: 'Comissão Parcelada' },
  { value: 'comissao_cavalo', label: '🐴 Cavalo' }
]

export default function OrganizadorDetalhes() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo, loadOrganizadorCompleto } = useOrganizador()
  const [organizador, setOrganizador] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dados')
  const [usuarios, setUsuarios] = useState([])
  const [showUsuarioModal, setShowUsuarioModal] = useState(false)
  const [editingUsuario, setEditingUsuario] = useState(null)
  const [cepLoading, setCepLoading] = useState(false)
  const [searchCidade, setSearchCidade] = useState('')
  const [cidadesSugeridas, setCidadesSugeridas] = useState([])
  const [showCidadeSugestoes, setShowCidadeSugestoes] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    cpf_cnpj: '',
    email: '',
    phone: '',
    phone2: '',
    address: '',
    cidade: '',
    estado: '',
    cep: '',
    fluxo_leilao: 'comissao_primeira_parcela',
    conta_azul_id: '',
    link_catalogo: '',
    logo_url: '',
    produtos_premium: [],
    usa_comissao_socios: false,
    usa_comissao_por_qtd: false,
    somente_visualizacao: false,
    pisteiro_remoto_percentual: ''
  })

  useEffect(() => {
    loadOrganizador()
    loadUsuarios()
  }, [id])

  const loadOrganizador = async () => {
    try {
      setLoading(true)
      const data = await organizadoresService.getById(id)
      setOrganizador(data)

      const logoUrl = data.logo_url ?
        (data.logo_url.startsWith('http') ? data.logo_url : `${import.meta.env.VITE_API_URL}${data.logo_url}`) :
        ''

      setFormData({
        name: data.name,
        cpf_cnpj: data.cpf_cnpj || '',
        email: data.email,
        phone: data.phone || '',
        phone2: data.phone2 || '',
        address: data.address || '',
        cidade: data.cidade || '',
        estado: data.estado || '',
        cep: data.cep || '',
        fluxo_leilao: data.fluxo_leilao || 'comissao_primeira_parcela',
        conta_azul_id: data.conta_azul_id || '',
        link_catalogo: data.link_catalogo || '',
        logo_url: logoUrl,
        produtos_premium: data.produtos_premium || [],
        usa_comissao_socios: data.usa_comissao_socios || false,
        usa_comissao_por_qtd: data.usa_comissao_por_qtd || false,
        somente_visualizacao: data.somente_visualizacao || false,
        pisteiro_remoto_percentual: data.pisteiro_remoto_percentual ?? ''
      })
      setSearchCidade(data.cidade || '')
    } catch (error) {
      console.error('Erro ao carregar organizador:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCepChange = async (cep) => {
    const formattedCep = formatCep(cep)
    setFormData(prev => ({ ...prev, cep: formattedCep }))

    if (formattedCep.replace(/[^\d]/g, '').length === 8) {
      setCepLoading(true)
      try {
        const cepData = await fetchCEP(formattedCep)
        if (cepData) {
          setFormData(prev => ({
            ...prev,
            address: cepData.endereco || prev.address,
            cidade: cepData.cidade || prev.cidade,
            estado: cepData.estado || prev.estado
          }))
          setSearchCidade(cepData.cidade || '')
        }
      } finally {
        setCepLoading(false)
      }
    }
  }

  const handleCidadeInputChange = (valor) => {
    setSearchCidade(valor)
    setFormData(prev => ({ ...prev, cidade: valor }))
    setShowCidadeSugestoes(true)
  }

  const handleSelecionarCidade = (cidade) => {
    setFormData(prev => ({ ...prev, cidade: cidade.nome, estado: cidade.uf }))
    setSearchCidade(cidade.nome)
    setCidadesSugeridas([])
    setShowCidadeSugestoes(false)
  }

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (showCidadeSugestoes && searchCidade && searchCidade.length >= 2) {
        try {
          const resultado = await clientesService.searchCidades(searchCidade)
          setCidadesSugeridas(resultado)
        } catch (err) {
          console.error('Erro ao buscar cidades:', err)
          setCidadesSugeridas([])
        }
      } else {
        setCidadesSugeridas([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchCidade, showCidadeSugestoes])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const payload = {
        ...formData,
        pisteiro_remoto_percentual: formData.pisteiro_remoto_percentual === '' ? null : Number(formData.pisteiro_remoto_percentual),
      }
      await organizadoresService.update(id, payload)
      await loadOrganizador()
      if (organizadorAtivo?.id === parseInt(id)) {
        await loadOrganizadorCompleto(id)
      }
      success('Organizador atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao salvar organizador:', error)
      error('Erro ao salvar organizador')
    }
  }

  const loadUsuarios = async () => {
    try {
      const data = await usuariosService.getByOrganizador(id)
      setUsuarios(data || [])
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
      setUsuarios([])
    }
  }

  const handleSaveUsuario = async (usuarioId, userData) => {
    try {
      if (usuarioId) {
        await usuariosService.update(id, usuarioId, userData)
      } else {
        await usuariosService.create(id, userData)
      }
      await loadUsuarios()
    } catch (error) {
      throw error
    }
  }

  const handleToggleUsuarioStatus = async (usuario) => {
    try {
      await usuariosService.toggleStatus(id, usuario.id)
      await loadUsuarios()
      success(`Usuário ${usuario.is_active ? 'inativado' : 'ativado'} com sucesso!`)
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error)
      error('Erro ao alterar status do usuário')
    }
  }

  const handleEditUsuario = (usuario) => {
    setEditingUsuario(usuario)
    setShowUsuarioModal(true)
  }

  const handleNewUsuario = () => {
    setEditingUsuario(null)
    setShowUsuarioModal(true)
  }

  const handleCloseUsuarioModal = () => {
    setShowUsuarioModal(false)
    setEditingUsuario(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue"></div>
      </div>
    )
  }

  if (!organizador) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Organizador não encontrado</p>
      </div>
    )
  }

  const tabs = [
    { id: 'dados',        label: 'Dados',     icon: User },
    { id: 'endereco',     label: 'Endereço',  icon: MapPin },
    { id: 'configuracao', label: 'Config.',   icon: Settings },
    { id: 'integracoes',  label: 'Integr.',   icon: LinkIcon },
    { id: 'logo',         label: 'Logo',      icon: Image },
    { id: 'produtos',     label: 'Premium',   icon: Settings },
    { id: 'usuarios',     label: 'Usuários',  icon: Users }
  ]

  return (
    <>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/organizadores')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{organizador.name}</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Organizadores &gt; {organizador.name}
            </p>
          </div>
        </div>

        {/* Tabs — scrollable horizontalmente no mobile */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-rematei-blue text-rematei-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-4 sm:p-6">
            {/* Dados Básicos */}
            {activeTab === 'dados' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Leiloeira *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="input-rematei"
                      placeholder="Ex: Leilões Central"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPF/CNPJ
                    </label>
                    <input
                      type="text"
                      value={formData.cpf_cnpj}
                      onChange={(e) => {
                        const formatted = formatCpfCnpj(e.target.value)
                        setFormData({...formData, cpf_cnpj: formatted})
                      }}
                      className="input-rematei"
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      maxLength="18"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-rematei"
                    placeholder="contato@leiloeira.com.br"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone Principal
                    </label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-rematei"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone 2
                    </label>
                    <input
                      type="text"
                      value={formData.phone2}
                      onChange={(e) => setFormData({...formData, phone2: e.target.value})}
                      className="input-rematei"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Endereço */}
            {activeTab === 'endereco' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="sm:w-1/3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CEP
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.cep}
                      onChange={(e) => handleCepChange(e.target.value)}
                      className="input-rematei"
                      placeholder="00000-000"
                      maxLength="9"
                    />
                    {cepLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full" />
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Preenche endereço, cidade e estado automaticamente
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço Completo
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="input-rematei"
                    rows="3"
                    placeholder="Rua, número, bairro, complemento..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                  <div className="sm:col-span-2 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={searchCidade}
                      onChange={(e) => handleCidadeInputChange(e.target.value)}
                      onFocus={() => setShowCidadeSugestoes(true)}
                      onBlur={() => setTimeout(() => setShowCidadeSugestoes(false), 150)}
                      className="input-rematei"
                      placeholder="Nome da cidade"
                      autoComplete="off"
                    />
                    {showCidadeSugestoes && cidadesSugeridas.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {cidadesSugeridas.map((cidade, index) => (
                          <li
                            key={`${cidade.nome}-${cidade.uf}-${index}`}
                            onMouseDown={() => handleSelecionarCidade(cidade)}
                            className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer flex justify-between"
                          >
                            <span>{cidade.nome}</span>
                            <span className="text-gray-400">{cidade.uf}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado
                    </label>
                    <input
                      type="text"
                      maxLength="2"
                      value={formData.estado}
                      onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                      className="input-rematei"
                      placeholder="UF"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Configuração */}
            {activeTab === 'configuracao' && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fluxo de Leilão
                  </label>
                  <select
                    value={formData.fluxo_leilao}
                    onChange={(e) => setFormData({...formData, fluxo_leilao: e.target.value})}
                    className="input-rematei"
                  >
                    {fluxoOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-2">
                    Define como será processada a comissão nos leilões
                  </p>
                  {formData.fluxo_leilao === 'comissao_cavalo' && (
                    <p className="text-xs text-amber-600 mt-2">
                      🐴 Organizadores com fluxo Cavalo só podem criar leilões do tipo Cavalo
                      (as demais opções de tipo de leilão ficam ocultas para eles).
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Integrações */}
            {activeTab === 'integracoes' && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID Conta Azul
                  </label>
                  <input
                    type="text"
                    value={formData.conta_azul_id}
                    onChange={(e) => setFormData({...formData, conta_azul_id: e.target.value})}
                    className="input-rematei"
                    placeholder="ID da integração com Conta Azul"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug do Catálogo Online
                  </label>
                  <div className="flex items-center">
                    <span className="hidden sm:inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm flex-shrink-0">
                      {window.location.origin}/catalogo/
                    </span>
                    <span className="sm:hidden inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-xs flex-shrink-0">
                      /catalogo/
                    </span>
                    <input
                      type="text"
                      value={formData.link_catalogo}
                      onChange={(e) => {
                        const slug = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, '')
                        setFormData({...formData, link_catalogo: slug})
                      }}
                      className="input-rematei rounded-l-none flex-1"
                      placeholder="minha-leiloeira"
                    />
                  </div>
                  {formData.link_catalogo && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <p className="text-xs text-gray-500 break-all">
                        URL pública: <span className="font-medium text-blue-600">{window.location.origin}/catalogo/{formData.link_catalogo}</span>
                      </p>
                      <a
                        href={`/catalogo/${formData.link_catalogo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 underline flex-shrink-0"
                      >
                        Abrir
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Logo */}
            {activeTab === 'logo' && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo da Leiloeira
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-rematei-blue hover:text-blue-500 focus-within:outline-none">
                          <span>Fazer upload</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0]
                              if (file) {
                                try {
                                  await organizadoresService.uploadLogo(id, file)
                                  await loadOrganizador()
                                  success('Logo atualizada com sucesso!')
                                } catch (error) {
                                  console.error('Erro no upload:', error)
                                  error('Erro ao fazer upload da logo')
                                }
                              }
                            }}
                          />
                        </label>
                        <p className="pl-1">ou arraste e solte</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF até 10MB
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ou insira URL da logo
                    </label>
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                      className="input-rematei"
                      placeholder="https://exemplo.com/logo.png"
                    />
                  </div>
                </div>

                {formData.logo_url && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                    <img
                      src={formData.logo_url}
                      alt="Preview da logo"
                      className="max-w-xs max-h-32 object-contain border border-gray-200 rounded bg-white p-2"
                      onError={(e) => { e.target.style.display = 'none' }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Produtos Premium */}
            {activeTab === 'produtos' && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Produtos Premium Rematei</h3>
                  <p className="text-sm text-gray-600">
                    Selecione os produtos premium que este organizador terá acesso
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {[
                    { id: 'filtros_baterias', label: 'Filtros Automáticos de Baterias', description: 'Ordem de entrada automática por bateria (sexo ou personalizado)', icon: '🔍' },
                    { id: 'quem_vendeu_trouxe', label: 'Quem Vendeu/Captou', description: 'Módulo Pisteiros + opção no lote/faturamento', icon: '🤝' },
                    { id: 'gestao_financeira', label: 'Gestão Financeira', description: 'Contas a pagar/receber + menu Gestão', icon: '💰' },
                    { id: 'gestao_socios', label: 'Gestão de Sócios', description: 'Controle de sociedades, vigência e renovação', icon: '🤝' },
                    { id: 'portaria', label: 'Entrada x Saída Animais', description: 'Controle de entrada e saída de animais', icon: '🚛' },
                    { id: 'inteligencia_pecuaria', label: 'Inteligência Pecuária', description: 'Mapa de compradores e vendedores por raça, tipo, idade e região', icon: '🧠' },
                    { id: 'assinatura_digital', label: 'Assinatura Digital', description: 'Integração com assinatura digital', icon: '✍️' },
                    { id: 'notas_fiscais', label: 'Emissão de Notas Fiscais', description: 'Acesso ao menu Notas Fiscais', icon: '📄' },
                    { id: 'pisteiro_remoto', label: 'Pisteiro Rematei', description: 'Lances remotos de pisteiros no leilão via Painel V2', icon: '🚀' },
                    { id: 'analise_360', label: 'Análise 360° do Cliente', description: 'IA comportamental + Score Rematei', icon: '📊' },
                    { id: 'comissao_socios', label: 'Comissão por Sócios', description: 'Sistema de comissões diferenciadas para sócios', icon: '👥' },
                    { id: 'comissao_por_qtd', label: 'Comissão por Quantidade de Animais', description: 'Tabela de comissão de venda por faixa de quantidade de animais do lote (substitui a comissão de venda padrão)', icon: '🐂' },
                    { id: 'exportacao_vendas', label: 'Exportação de Vendas', description: 'Botão "Exportar" na tela de Lotes — planilha (Excel/CSV) com todos os dados de vendas e pagamentos', icon: '📤' },
                    { id: 'upload_video_lote', label: 'Upload de Vídeo no Lote', description: 'Permite anexar 1 vídeo por lote (até 100MB), exibido no catálogo online. Apagado automaticamente 7 dias após a data do leilão', icon: '🎥' },
                    { id: 'link_cadastro', label: 'Link de Cadastro Público', description: 'Formulário público personalizável para captação de novos clientes, com dados caindo direto no cadastro de Cliente', icon: '🔗' },
                    { id: 'prazo_comissao', label: 'Prazo da Comissão + Nota Promissória', description: 'Mapa/Fatura do Comprador (fluxo comissão na primeira parcela): define o prazo de pagamento da comissão e despesas, grava o vencimento na fatura, na gestão e nas comissões por cliente, e imprime a nota promissória da comissão', icon: '📅' },
                    { id: 'isencao_defesa', label: 'Isenção de Comissão de Defesa', description: 'Configuração em Configurações > Isenções Defesa: mostra o botão "Isentar Defesa" só pra vendedores com % defendido abaixo do limite configurado', icon: '🛡️' }
                  ].map((produto) => (
                    <div key={produto.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-blue-300 transition-colors">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.produtos_premium.includes(produto.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newProdutos = [...formData.produtos_premium, produto.id]
                              setFormData({
                                ...formData,
                                produtos_premium: newProdutos,
                                usa_comissao_socios: newProdutos.includes('comissao_socios'),
                                usa_comissao_por_qtd: newProdutos.includes('comissao_por_qtd')
                              })
                            } else {
                              const newProdutos = formData.produtos_premium.filter(p => p !== produto.id)
                              setFormData({
                                ...formData,
                                produtos_premium: newProdutos,
                                usa_comissao_socios: newProdutos.includes('comissao_socios'),
                                usa_comissao_por_qtd: newProdutos.includes('comissao_por_qtd')
                              })
                            }
                          }}
                          className="mt-1 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-base">{produto.icon}</span>
                            <h4 className="font-medium text-gray-900 text-sm">{produto.label}</h4>
                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                              Premium
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600">{produto.description}</p>
                        </div>
                      </label>

                      {produto.id === 'pisteiro_remoto' && formData.produtos_premium.includes('pisteiro_remoto') && (
                        <div className="mt-3 ml-7 flex items-center gap-2">
                          <label className="text-xs sm:text-sm text-gray-700">
                            % de comissão Rematei (sobre a comissão de compra)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            placeholder="10"
                            value={formData.pisteiro_remoto_percentual}
                            onChange={(e) => setFormData({ ...formData, pisteiro_remoto_percentual: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                          <span className="text-xs text-gray-500">%</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Controle de Acesso */}
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Controle de Acesso</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Defina o nível de acesso dos usuários deste organizador ao sistema.
                  </p>
                  <div className="border border-orange-200 rounded-lg p-3 sm:p-4 bg-orange-50">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.somente_visualizacao}
                        onChange={(e) => setFormData({ ...formData, somente_visualizacao: e.target.checked })}
                        className="mt-1 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-base">👁️</span>
                          <h4 className="font-medium text-gray-900 text-sm">Modo Somente Visualização</h4>
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                            Migração
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600">
                          Quando ativado, os usuários conseguem fazer login e visualizar todos os dados, mas não podem criar ou editar leilões, lotes, clientes e gestão financeira.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-blue-500 text-lg flex-shrink-0">ℹ️</div>
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1 text-sm">Sobre os Produtos Premium</h4>
                      <p className="text-xs sm:text-sm text-blue-800">
                        Os produtos premium são funcionalidades avançadas que expandem as capacidades do sistema.
                        Cada produto habilitado aqui ficará disponível para todos os usuários deste organizador.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            {activeTab !== 'usuarios' && (
              <div className="flex justify-end pt-4 sm:pt-6 border-t border-gray-200 mt-6 sm:mt-8">
                <button type="submit" className="btn-primary">
                  Salvar Alterações
                </button>
              </div>
            )}
          </form>

          {/* Usuários — mesma tela completa de Configurações > Usuários */}
          {activeTab === 'usuarios' && (
            <div className="p-4 sm:p-6">
              <Usuarios organizadorId={Number(id)} />
            </div>
          )}
        </div>
      </div>

    </>
  )
}
